// ─────────────────────────────────────────────────────────────────────────────
// algolia-shared.js — CB Shopify theme
//
// Bootstraps window.CBAlgolia with shared utilities, config snapshot, and icon
// factories used by all other Algolia hook files. Must be the first Algolia
// script tag in snippets/algolia-scripts.liquid so its algolia.hooks.initialize
// listener fires before those of the template and swatch files.
//
// window.algoliaSettings / algoliaCollectionData are set by an inline <script>
// in snippets/algolia-scripts.liquid, which runs synchronously in <head> before
// any deferred scripts — so they are safe to read immediately here.
// ─────────────────────────────────────────────────────────────────────────────

;(function () {
  // ── Namespace ─────────────────────────────────────────────────────────────
  window.CBAlgolia = window.CBAlgolia || {}

  // ── Config snapshot ───────────────────────────────────────────────────────
  // Read once so all files get the same frozen snapshot.
  const settings = window.algoliaSettings || {}
  const collectionData = window.algoliaCollectionData || {}

  window.CBAlgolia.settings = settings
  window.CBAlgolia.collectionData = collectionData

  // Alignment suffix — derived from settings (doesn't change per-card)
  window.CBAlgolia.alignSuffix =
    settings.productContentAlignment === 'left'
      ? 'start'
      : settings.productContentAlignment === 'right'
        ? 'end'
        : 'center'

  // ── URL helpers ───────────────────────────────────────────────────────────

  // Reads active meta.custom.base_colors refinements from the current URL.
  // Re-evaluated on every call so it picks up the latest filter state after
  // Algolia updates the URL on each refinement change.
  window.CBAlgolia.getActiveBaseColors = function getActiveBaseColors() {
    const params = new URLSearchParams(window.location.search)
    const colors = []
    for (const [key, value] of params.entries()) {
      if (/refinementList\[meta\.custom\.base_colors]\[\d+]/.test(key)) {
        colors.push(value)
      }
    }
    return colors
  }

  // ── Image helpers ─────────────────────────────────────────────────────────

  // Handles both Shopify CDN formats:
  //   New: ?width=1200  →  replace width param  e.g. photo.jpg?v=x&width=1200
  //   Legacy: _master   →  replace suffix        e.g. photo_master.jpg
  window.CBAlgolia.makeShopifyImageSrc = function makeShopifyImageSrc(
    url,
    width
  ) {
    if (!url) return url
    if (url.includes('_master')) return url.replace('_master', `_${width}x`)
    return url.replace(/([?&]width=)\d+/, `$1${width}`)
  }

  // Generates a srcset string for URLs that contain a width param.
  // (swatchdata returns width:1200 URLs; makeShopifyImageSrc replaces it.)
  // Note: hit.images[] URLs from Algolia have no width param — use &width= there.
  window.CBAlgolia.makeShopifySrcset = function makeShopifySrcset(url) {
    return [165, 360, 533, 720, 940, 1066]
      .map((w) => `${window.CBAlgolia.makeShopifyImageSrc(url, w)} ${w}w`)
      .join(', ')
  }

  window.CBAlgolia.updateHoverImage = function updateHoverImage(
    cardEl,
    hoverUrl
  ) {
    const primaryImg = cardEl.querySelector('[data-algolia-primary-img]')
    if (!primaryImg) return
    const media = primaryImg.parentElement
    let hoverEl = media.querySelector('.secondary__img')

    if (hoverUrl) {
      if (!hoverEl) {
        hoverEl = document.createElement('img')
        hoverEl.loading = 'lazy'
        hoverEl.className = 'motion-reduce secondary__img'
        hoverEl.sizes = primaryImg.sizes
        media.appendChild(hoverEl)
      }
      hoverEl.src = window.CBAlgolia.makeShopifyImageSrc(hoverUrl, 533)
      hoverEl.srcset = window.CBAlgolia.makeShopifySrcset(hoverUrl)
      hoverEl.alt = primaryImg.alt
    } else if (hoverEl) {
      hoverEl.remove()
    }
  }

  // ── Money helpers ─────────────────────────────────────────────────────────

  // Formats a price in **cents** (integer) using the shop's money format.
  // Handles all five Shopify money format tokens.
  window.CBAlgolia.formatMoney = function formatMoney(cents) {
    const moneyFormat = settings.moneyFormat || '${{amount}}'
    const amount = (cents / 100).toFixed(2)
    const amountNoDec = String(Math.round(cents / 100))
    const amountComma = amount.replace('.', ',')
    const amountNoDecComma = amountNoDec.replace('.', ',')
    const amountApostrophe = amount.replace('.', "'")
    return moneyFormat
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecComma)
      .replace('{{amount_with_comma_separator}}', amountComma)
      .replace('{{amount_with_apostrophe_separator}}', amountApostrophe)
      .replace('{{amount_no_decimals}}', amountNoDec)
      .replace('{{amount}}', amount)
  }

  // Wraps formatMoney in the "From {{ price }}" locale template.
  // fromPriceLabel is injected via algolia-scripts.liquid; falls back gracefully.
  window.CBAlgolia.fromPrice = function fromPrice(cents) {
    const formatted = window.CBAlgolia.formatMoney(cents)
    const template = settings.fromPriceLabel || 'From __PRICE__'
    return template.replace('__PRICE__', formatted)
  }

  // Returns the lowest-priced visible variant from a swatchdata array.
  // Prefers available variants; if none are available, returns the cheapest
  // regardless of availability. Returns null when the array is empty.
  window.CBAlgolia.lowestVisibleVariant = function lowestVisibleVariant(
    variants
  ) {
    const priced = (variants || []).filter((v) => v.price > 0)
    if (!priced.length) return null
    const available = priced.filter((v) => v.available)
    const pool = available.length ? available : priced
    return pool.reduce((min, v) => (v.price < min.price ? v : min), pool[0])
  }

  // ── Icon factories ────────────────────────────────────────────────────────
  // Each icon is a factory that accepts the html tagged template provided by
  // Algolia's hook callback. This lets the returned nodes participate in the
  // same virtual-DOM reconciliation as the rest of the template.
  //
  // Usage inside a hook:
  //   const caret = CBAlgolia.icons.caret(html)
  //   return html`<div>${caret}</div>`
  window.CBAlgolia.icons = {
    caret(html) {
      return html`<svg
        xmlns="http://www.w3.org/2000/svg"
        class="icon icon-caret"
        viewBox="0 0 512 512"
        fill="currentColor"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M98 190.06l139.78 163.12a24 24 0 0036.44 0L414 190.06c13.34-15.57 2.28-39.62-18.22-39.62h-279.6c-20.5 0-31.56 24.05-18.18 39.62z"
        />
      </svg>`
    },

    arrow(html) {
      return html`<svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        class="icon icon-arrow"
        viewBox="0 0 14 10"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fill="currentColor"
          fill-rule="evenodd"
          d="M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546"
          clip-rule="evenodd"
        />
      </svg>`
    },

    // Shared by both the filter drawer summary close button and the
    // quick-add modal close button — identical path.
    close(html) {
      return html`<svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        class="icon icon-close"
        viewBox="0 0 18 17"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fill="currentColor"
          d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z"
        />
      </svg>`
    },

    cart(html) {
      return html`<svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fill="currentColor"
          d="M15 7H9V1C9 0.734784 8.89464 0.48043 8.70711 0.292893C8.51957 0.105357 8.26522 0 8 0C7.73478 0 7.48043 0.105357 7.29289 0.292893C7.10536 0.48043 7 0.734784 7 1V7H1C0.734784 7 0.48043 7.10536 0.292893 7.29289C0.105357 7.48043 0 7.73478 0 8C0 8.26522 0.105357 8.51957 0.292893 8.70711C0.48043 8.89464 0.734784 9 1 9H7V15C7 15.2652 7.10536 15.5196 7.29289 15.7071C7.48043 15.8946 7.73478 16 8 16C8.26522 16 8.51957 15.8946 8.70711 15.7071C8.89464 15.5196 9 15.2652 9 15V9H15C15.2652 9 15.5196 8.89464 15.7071 8.70711C15.8946 8.51957 16 8.26522 16 8C16 7.73478 15.8946 7.48043 15.7071 7.29289C15.5196 7.10536 15.2652 7 15 7Z"
        />
      </svg>`
    },
  }
})()
